const { useEffect, useState } = React;
import { dbCountBoardsInRange, dbListTemplates } from "../data.jsx";
import { useT } from "../i18n.jsx";
import { toKey, weekdayOf } from "../model.jsx";
import { SponsorPanel } from "../sponsors.jsx";
import { CalendarIcon, LayoutTemplate, Loader2, Lock, Radio, StarIcon, T } from "../theme.jsx";

// ================================================================== HOME =====

function HomeScreen({ perms, viewer, liveNowKey, onGoLive, onNavigate, onOpenDay }) {
  const { lang, t } = useT();
  const [stats, setStats] = useState({ templates: null, lockedThisMonth: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const now = new Date();
      const startKey = toKey(new Date(now.getFullYear(), now.getMonth(), 1));
      const endKey = toKey(new Date(now.getFullYear(), now.getMonth() + 1, 1));
      const [templates, lockedThisMonth] = await Promise.all([
        dbListTemplates(),
        dbCountBoardsInRange(startKey, endKey),
      ]);
      if (!cancelled) setStats({ templates: templates.length, lockedThisMonth });
    })();
    return () => { cancelled = true; };
  }, []);

  const today = new Date();
  const upcoming = Array.from({ length: 5 }).map((_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
    return { date: d, key: toKey(d), wd: weekdayOf(d) };
  });

  const statTiles = [
    { label: t("statTemplates"), value: stats.templates, icon: LayoutTemplate },
    { label: t("statLockedMonth"), value: stats.lockedThisMonth, icon: Lock },
    { label: t("statShowsPerWeek"), value: 7, icon: CalendarIcon },
    { label: liveNowKey ? t("statLiveOn") : t("statLiveOff"), value: liveNowKey ? t("liveYes") : t("liveNo"), icon: Radio, danger: !!liveNowKey },
  ];

  const modules = [
    { key: "calendar", label: t("navCalendar"), desc: t("moduleCalendarDesc"), icon: CalendarIcon },
    { key: "master", label: t("navMaster"), desc: t("moduleMasterDesc"), icon: StarIcon },
    { key: "template", label: t("navTemplate"), desc: t("moduleTemplateDesc"), icon: LayoutTemplate },
  ];

  return (
    <div style={{ padding: "22px 20px 40px", maxWidth: 1080, margin: "0 auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 20 }}>
        {statTiles.map((s) => (
          <div key={s.label} style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: s.danger ? T.danger : T.subtext, marginBottom: 6 }}>
              <s.icon size={13} />
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.3 }}>{s.label.toUpperCase()}</span>
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: s.danger ? T.danger : T.text }}>
              {s.value === null ? <Loader2 size={18} style={{ animation: "rb-spin 1s linear infinite" }} /> : s.value}
            </div>
          </div>
        ))}
      </div>

      {liveNowKey && (
        <button className="rb-btn" onClick={() => onGoLive(liveNowKey)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: T.danger, border: "none", borderRadius: 12, padding: "12px 16px", color: "#fff", fontSize: 13.5, fontWeight: 800, cursor: "pointer", marginBottom: 20 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff", animation: "rb-pulse 1.2s infinite" }} /> {t("liveBannerBtn")} ({liveNowKey}) {t("liveBannerBtnSuffix")}
        </button>
      )}

      <SponsorPanel canEdit={!!(perms && perms.canMaster)} todayWd={weekdayOf(today)} />

      <div style={{ fontSize: 12.5, fontWeight: 800, color: T.subtext, marginBottom: 8, letterSpacing: 0.3 }}>{t("upcomingDays")}</div>
      <div className="rb-scroll" style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 6, marginBottom: 22 }}>
        {upcoming.map(({ date, key, wd }, i) => (
          <button key={key} className="rb-btn" onClick={() => (onOpenDay ? onOpenDay(date) : onNavigate("calendar"))}
            style={{ flex: "0 0 132px", textAlign: "left", background: T.panel, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12, cursor: "pointer" }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: i === 0 ? T.accent : T.subtext }}>{i === 0 ? t("today") : (lang === "en" ? wd.shortEn : wd.short)}</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: T.text, marginTop: 2 }}>{date.getDate()}</div>
            <div style={{ fontSize: 11, color: T.subtext, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{wd.code}</div>
          </button>
        ))}
      </div>

      <div style={{ fontSize: 12.5, fontWeight: 800, color: T.subtext, marginBottom: 8, letterSpacing: 0.3 }}>{t("modules")}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {modules.map((m) => (
          <button key={m.key} className="rb-btn" onClick={() => onNavigate(m.key)}
            style={{ textAlign: "left", background: T.panel, border: `1px solid ${T.border}`, borderRadius: 14, padding: 18, cursor: "pointer" }}>
            <m.icon size={18} color={T.accent} />
            <div style={{ fontSize: 14.5, fontWeight: 800, color: T.text, marginTop: 10 }}>{m.label}</div>
            <div style={{ fontSize: 12, color: T.subtext, marginTop: 4, lineHeight: 1.4 }}>{m.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export { HomeScreen };
